import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import dayjs, { Dayjs } from 'dayjs';
import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../../../contexts/AuthContext';
import { useAgreements } from '../../../../hooks/api/useAgreements';
import { useBranding } from '../../../../hooks/api/useBranding';
import { useGetI18n } from '../../../../hooks/api/useI18n';
import useFeatureFlags from '../../../../hooks/useFeatureFlag';
import { Agreements } from '../../../../types/Agreements.types';
import { Journals } from '../../../../types/Journals.types';
import { DateRangeFields } from '../../../../components/inputs/DateRangeFields';
import { AgreementNameField } from './fields/AgreementNameField';
import { CollectionsField } from './fields/CollectionsField';
import { ExtraInfoField } from './fields/ExtraInfoField';
import { FileUploadField } from './fields/FileUploadField';
import { DescriptionSection } from './fields/DescriptionSection';
import { InternalInformationSection } from './fields/InternalInformationSection';
import { LicensesField } from './fields/LicensesField';
import { PaymentFields } from './fields/PaymentFields';
import { PublisherField } from './fields/PublisherField';
import { FormDataType } from './types';
import { env } from '../../../../env';
import { hasExtension } from '../../../../utils/branding';

export interface AgreementFormFieldsProps {
  isAdding: boolean;
  agreementId: string | undefined;
  agreement: Agreements | undefined;
  onPreviewJournals: (journals: Journals[]) => void;
  loading?: boolean;
  onSubmit: (formData: FormDataType) => void;
  onChange?: (formData: FormDataType) => void;
  publishers: string[];
  paymentOptions: string[];
  selectableLicenses: string[];
  initialFormData: FormDataType;
}

const AgreementFormFieldsComponent: React.FC<AgreementFormFieldsProps> = ({
  isAdding,
  agreementId,
  agreement,
  onPreviewJournals,
  loading,
  onSubmit,
  onChange,
  paymentOptions,
  publishers,
  selectableLicenses,
  initialFormData,
}) => {
  const { t } = useTranslation();
  const { impersonationLock } = useAuthContext();
  const { branding } = useBranding();
  const { data: i18nData } = useGetI18n();
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormDataType>(initialFormData);
  const [changesMade, setChangesMade] = useState<boolean>(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState<boolean>(false);
  const [tab, setTab] = useState<'details' | 'collections'>('details');

  const hasCollectionsExtension = hasExtension(branding, 'collections');

  const startDate = useMemo(() => {
    if (!formData.start_date) return null;
    const date = dayjs(formData.start_date, 'YYYY-MM-DD');
    return date.isValid() ? date : null;
  }, [formData.start_date]);

  const endDate = useMemo(() => {
    if (!formData.end_date) return null;
    const date = dayjs(formData.end_date, 'YYYY-MM-DD');
    return date.isValid() ? date : null;
  }, [formData.end_date]);

  const dateRangeError = useMemo(() => {
    if (!startDate || !endDate) return undefined;
    return startDate.isAfter(endDate)
      ? (t('agreements.errors.startDateAfterEndDate') as string)
      : undefined;
  }, [startDate, endDate, t]);

  const hasI18nExtension = useMemo(() => {
    return Object.keys(branding?.extensions ?? {}).includes('i18n');
  }, [branding]);

  const locales = useMemo(
    () => (i18nData ? Object.keys(i18nData) : ['en']),
    [i18nData]
  );

  const requiredFieldsDone = useMemo(() => {
    return (
      formData.name &&
      formData.publisher &&
      formData.payment &&
      (formData.payment_url || hasI18nExtension) &&
      formData.start_date &&
      formData.end_date &&
      formData.file &&
      !!startDate &&
      !!endDate &&
      endDate.isAfter(startDate) &&
      !isPreviewLoading
    );
  }, [
    endDate,
    formData.end_date,
    formData.file,
    formData.name,
    formData.payment,
    formData.payment_url,
    formData.publisher,
    formData.start_date,
    hasI18nExtension,
    startDate,
    isPreviewLoading,
  ]);
  const { previewAgreement } = useAgreements();
  /** USED TO ENABLE FILE PREVIEW ONLY IN STAGING */
  const { enableFilePreviews } = useFeatureFlags();

  const SubmitButton = useMemo(() => {
    if (isAdding) {
      return (
        <div style={{ paddingTop: 14 }}>
          <Box sx={{ mb: 10 }}>
            <Button
              variant="primary"
              disabled={!requiredFieldsDone || impersonationLock}
              type="submit"
            >
              Create
            </Button>
            {loading && (
              <CircularProgress
                size={24}
                sx={{
                  color: 'white',
                  position: 'absolute',
                  marginTop: '6px',
                  marginLeft: '-78px',
                }}
              />
            )}
          </Box>
          <Typography variant="body1">
            {t('agreements.asteriskRequiredContinue')}
          </Typography>
        </div>
      );
    }
    return (
      <Box
        sx={{
          paddingTop: 14,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 14,
          alignItems: { xs: 'flex-start', sm: 'center' },
        }}
      >
        <Button
          variant="primary"
          disabled={!changesMade || impersonationLock}
          type="submit"
        >
          {loading ? (
            <>
              <span>Saving changes</span>
              <CircularProgress
                size={24}
                sx={{
                  color: 'white',
                  marginLeft: '6px',
                }}
              />
            </>
          ) : (
            'Save changes'
          )}
        </Button>
        {agreement?.updated_at && agreement?.updated_by && (
          <Typography variant="body2">
            {t('agreements.lastUpdatedByOn', {
              email: agreement.updated_by,
              date: dayjs
                .utc(agreement.updated_at)
                .local()
                .format('YYYY-MM-DD HH:mm'),
            })}
          </Typography>
        )}
      </Box>
    );
  }, [
    isAdding,
    changesMade,
    impersonationLock,
    agreement,
    t,
    requiredFieldsDone,
    loading,
  ]);

  useEffect(() => {
    if (!agreementId || !agreement || isLoaded || !branding) return;

    let description;
    try {
      description =
        typeof agreement.description === 'string'
          ? JSON.parse(agreement.description)
          : (agreement.description ?? {});
    } catch {
      description = {};
    }
    setFormData({
      ...formData,
      name: agreement.name ?? '',
      publisher: agreement.publisher ?? '',
      payment: agreement.payment ?? '',
      payment_url:
        !hasI18nExtension && !agreement.payment_url
          ? 'Could not fetch URL...'
          : (agreement.payment_url ?? ''),
      description,
      notes: agreement.notes ?? '',
      licenses: agreement.licenses ?? [],
      start_date: agreement.start_date ?? '',
      end_date: agreement.end_date ?? '',
      extra_info: agreement.extra_info ?? '',
      status: agreement.status ?? 'enabled',
      collections: agreement.collections ?? [],
    });
    setIsLoaded(true);
  }, [
    agreementId,
    agreement,
    publishers,
    formData,
    isLoaded,
    hasI18nExtension,
    branding,
  ]);

  const handleFileUpload = useCallback(
    async (event: any) => {
      const file = event.target.files?.[0];
      const name = 'file';
      if (file) {
        const newFormData = { ...formData, [name]: file };
        setFormData(newFormData);
        onChange?.(newFormData);
      }
      if (!changesMade && !isAdding) {
        setChangesMade(true);
      }
      if (isAdding && enableFilePreviews) {
        setIsPreviewLoading(true);
        try {
          const requestBody: FormData = new FormData();
          requestBody.append('name', formData.name ?? 'Preview');
          requestBody.append(
            'publisher',
            formData.publisher ?? 'Aalborg University'
          );
          requestBody.append(
            'start_date',
            formData.start_date ?? '2023-01-01 00:00:00 UTC'
          );
          requestBody.append(
            'end_date',
            formData.end_date ?? '2030-01-01 00:00:00 UTC'
          );
          requestBody.append(
            'payment_url',
            formData.payment_url ?? 'https://www.wikipedia.org'
          );
          requestBody.append('file', file);
          requestBody.append('payment', formData.payment ?? 'Discounted');
          requestBody.append(
            'description',
            JSON.stringify(formData.description ?? {})
          );
          requestBody.append('notes', formData.notes ?? 'Preview');
          requestBody.append('extra_info', formData.extra_info ?? 'Preview');
          requestBody.append('status', formData.status ?? 'enabled');
          if (formData.licenses.length > 0) {
            for (const license of formData.licenses) {
              requestBody.append('licenses[]', license);
            }
          } else {
            requestBody.append('licenses[]', 'CC BY-NC');
          }
          const preview: Agreements = await previewAgreement(requestBody);
          onPreviewJournals(preview.journals ?? []);
        } finally {
          setIsPreviewLoading(false);
        }
      }
    },
    [
      changesMade,
      isAdding,
      enableFilePreviews,
      formData,
      onChange,
      previewAgreement,
      onPreviewJournals,
    ]
  );
  const getEmailString = useCallback((email: string) => {
    if (email.startsWith('mailto:')) {
      return email;
    } else if (email.includes('@')) {
      return `mailto:${email}`;
    } else {
      return email;
    }
  }, []);

  const sanitizePaymentUrl = useCallback((url: string) => {
    return url.replace('mailto:', '');
  }, []);
  const inputChangeHandler = useCallback(
    (
      event: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >
    ) => {
      const { name, value } = event.target;
      const newFormData = { ...formData, [name]: value };
      setFormData(newFormData);
      onChange?.(newFormData);

      if (!changesMade && !isAdding) {
        setChangesMade(true);
      }
    },
    [formData, onChange, changesMade, isAdding]
  );

  const inputDateHandler = useCallback(
    (name: string, date: Dayjs | null) => {
      const dateString = dayjs(date).format('YYYY-MM-DD HH:mm:ss UTC');
      const newFormData = { ...formData, [name]: dateString };
      setFormData(newFormData);
      onChange?.(newFormData);

      if (!changesMade && !isAdding) {
        setChangesMade(true);
      }
    },
    [formData, onChange, changesMade, isAdding]
  );

  const inputSelectChangeHandler = useCallback(
    (event: SelectChangeEvent) => {
      const { name, value } = event.target;

      const newFormData = {
        ...formData,
        [name]: value,
      };
      setFormData(newFormData);
      onChange?.(newFormData);

      if (!changesMade && !isAdding) {
        setChangesMade(true);
      }
    },
    [formData, onChange, changesMade, isAdding]
  );

  const inputSyntheticEventHandler = useCallback(
    (_event: SyntheticEvent, value: string | null) => {
      if (value) {
        const newFormData = { ...formData, publisher: value };
        setFormData(newFormData);
        onChange?.(newFormData);

        if (!changesMade && !isAdding) {
          setChangesMade(true);
        }
      }
    },
    [formData, onChange, changesMade, isAdding]
  );

  const inputMultipleHandler = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      const choices = event.target.value as string[];
      const newFormData = { ...formData, [event.target.name]: choices };
      setFormData(newFormData);
      onChange?.(newFormData);

      if (!changesMade && !isAdding) {
        setChangesMade(true);
      }
    },
    [formData, onChange, changesMade, isAdding]
  );

  const collectionsChangeHandler = useCallback(
    (values: string[]) => {
      const newFormData = { ...formData, collections: values };
      setFormData(newFormData);
      onChange?.(newFormData);
      if (!changesMade && !isAdding) setChangesMade(true);
    },
    [formData, onChange, changesMade, isAdding]
  );

  const paymentIcon = useMemo(() => {
    const payment = formData.payment;
    if (!payment || !i18nData) return undefined;

    const availableLocales = Object.keys(i18nData);
    // TODO: Update when language selector is implemented
    const locale = i18nData.en ? 'en' : availableLocales[0] || 'en';

    const payments = i18nData?.[locale]?.payments as
      | Record<string, { icon?: string }>
      | undefined;

    return payments?.[payment]?.icon;
  }, [formData.payment, i18nData]);

  const baseAssetUrl = env.PUBLIC_SEARCH_URL;

  const isNewFileSelected = typeof formData.file === 'object' && changesMade;
  const file = isNewFileSelected
    ? formData.file
    : agreement?.file_name || formData.file;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        const paymentUrl =
          formData.payment === 'Contact Library'
            ? getEmailString(formData.payment_url)
            : formData.payment_url;
        const submissionData = { ...formData, payment_url: paymentUrl };
        onSubmit(submissionData);
        setChangesMade(false);
      }}
    >
      {hasCollectionsExtension && (
        <Box sx={{ my: 24 }}>
          <Tabs
            value={tab}
            onChange={(_e, newTab) => setTab(newTab)}
            selectionFollowsFocus
            aria-label="Agreement form tabs"
          >
            <Tab
              value="details"
              id="tab-details"
              aria-controls="panel-details"
              label={t('agreements.tabs.details', 'Details')}
            />
            <Tab
              value="collections"
              id="tab-collections"
              aria-controls="panel-collections"
              label={t('agreements.tabs.collections', 'Collections')}
            />
          </Tabs>
        </Box>
      )}
      <Box
        {...(hasCollectionsExtension
          ? {
              role: 'tabpanel',
              id: 'panel-details',
              'aria-labelledby': 'tab-details',
              hidden: tab !== 'details',
            }
          : {})}
        sx={{ mb: 24 }}
      >
        <Grid container>
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                width: { xs: '100%', md: '90%', lg: '90%' },
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
                mt: 16,
              }}
            >
              <Box>
                <PublisherField
                  value={formData.publisher}
                  publishers={publishers}
                  disabled={impersonationLock}
                  onChange={(value) => {
                    inputSyntheticEventHandler({} as SyntheticEvent, value);
                  }}
                />
              </Box>
              <Box>
                <AgreementNameField
                  value={formData.name}
                  disabled={impersonationLock}
                  onChange={inputChangeHandler}
                />
              </Box>
              <Box>
                <PaymentFields
                  payment={formData.payment}
                  paymentUrl={formData.payment_url}
                  paymentOptions={paymentOptions}
                  paymentIcon={paymentIcon}
                  baseAssetUrl={baseAssetUrl}
                  hasI18nExtension={hasI18nExtension}
                  disabled={impersonationLock}
                  onPaymentChange={inputSelectChangeHandler}
                  onPaymentUrlChange={inputChangeHandler}
                  sanitizePaymentUrl={sanitizePaymentUrl}
                />
              </Box>
              <Box>
                <ExtraInfoField
                  value={formData.extra_info}
                  disabled={impersonationLock}
                  onChange={inputChangeHandler}
                />
              </Box>
              <DateRangeFields
                disabled={impersonationLock}
                error={dateRangeError}
                start={{
                  id: 'start_date',
                  label: (
                    <>
                      Start date <span aria-hidden="true">*</span>
                      <span className="sr-only">required</span>
                    </>
                  ),
                  value: startDate,
                  onChange: (date) => inputDateHandler('start_date', date),
                  pickerProps: {
                    format: 'YYYY-MM-DD',
                    maxDate: endDate || undefined,
                    slotProps: {
                      textField: {
                        inputProps: {
                          'data-testid': 'agreement-start-date-input',
                        },
                      },
                      openPickerButton: {
                        'data-testid': 'agreement-start-date-open-picker',
                      } as unknown as never,
                    },
                  },
                }}
                end={{
                  id: 'end_date',
                  label: (
                    <>
                      End date <span aria-hidden="true">*</span>
                      <span className="sr-only">required</span>
                    </>
                  ),
                  value: endDate,
                  onChange: (date) => inputDateHandler('end_date', date),
                  pickerProps: {
                    format: 'YYYY-MM-DD',
                    minDate: startDate || undefined,
                    slotProps: {
                      textField: {
                        inputProps: {
                          'data-testid': 'agreement-end-date-input',
                        },
                      },
                      openPickerButton: {
                        'data-testid': 'agreement-end-date-open-picker',
                      } as unknown as never,
                    },
                  },
                }}
                direction="row"
              />
              <Box>
                <LicensesField
                  value={formData.licenses}
                  options={selectableLicenses}
                  disabled={impersonationLock}
                  onChange={(e: SelectChangeEvent<string[]>) => {
                    inputMultipleHandler(e);
                  }}
                />
              </Box>
              <Box>
                <FileUploadField
                  value={file}
                  fileUpdatedAt={agreement?.file_updated_at}
                  fileUpdatedBy={agreement?.file_updated_by}
                  disabled={impersonationLock}
                  enableFilePreviews={enableFilePreviews}
                  onChange={handleFileUpload}
                  isLoading={isPreviewLoading}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={7}>
            <Box
              sx={{
                width: { xs: '100%', md: '95%', lg: '85%' },
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
                mt: 16,
              }}
            >
              {branding?.show_agreement_description && (
                <Box>
                  <DescriptionSection
                    description={formData.description}
                    locales={locales}
                    disabled={impersonationLock}
                    onChange={(value) => {
                      const newFormData = { ...formData, description: value };
                      setFormData(newFormData);
                      onChange?.(newFormData);
                      if (!changesMade && !isAdding) setChangesMade(true);
                    }}
                  />
                </Box>
              )}
              <Box>
                <InternalInformationSection
                  branding={branding}
                  description={formData.description}
                  locales={locales}
                  notes={formData.notes}
                  hide_agreement={formData.status === 'disabled'}
                  disabled={impersonationLock}
                  onDescriptionChange={(value) => {
                    const newFormData = { ...formData, description: value };
                    setFormData(newFormData);
                    onChange?.(newFormData);
                    if (!changesMade && !isAdding) setChangesMade(true);
                  }}
                  onNotesChange={inputChangeHandler}
                  onDisabledChange={(checked) => {
                    setFormData((prev) => ({
                      ...prev,
                      status: checked ? 'disabled' : 'enabled',
                    }));
                    setChangesMade(true);
                  }}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
      {hasCollectionsExtension && (
        <Box
          role="tabpanel"
          id="panel-collections"
          aria-labelledby="tab-collections"
          hidden={tab !== 'collections'}
          sx={{ mb: 24 }}
        >
          <CollectionsField
            value={formData.collections}
            collections={branding?.collections}
            disabled={impersonationLock}
            onChange={collectionsChangeHandler}
          />
        </Box>
      )}
      {SubmitButton}
    </form>
  );
};

const arePropsEqual = (
  prevProps: AgreementFormFieldsProps,
  nextProps: AgreementFormFieldsProps
) => {
  return (
    prevProps.agreement === nextProps.agreement &&
    prevProps.isAdding === nextProps.isAdding &&
    prevProps.loading === nextProps.loading &&
    prevProps.agreementId === nextProps.agreementId &&
    prevProps.publishers === nextProps.publishers &&
    prevProps.paymentOptions === nextProps.paymentOptions &&
    prevProps.selectableLicenses === nextProps.selectableLicenses
  );
};
export const AgreementFormFields = React.memo(
  AgreementFormFieldsComponent,
  arePropsEqual
);
